import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState, type JSX } from "react";

import { api } from "../../../lib/api.js";
import { errMsg, type ProfileUser } from "./profileTypes.js";

/**
 * Identity card — editable first/last name (PATCH `/api/profile`) with a
 * read-only email + role. Email is the SSO identity and is intentionally not
 * editable. Extracted from `ProfilePage.tsx`.
 */
export function IdentityCard({
  user,
  onSaved,
}: {
  user: ProfileUser;
  onSaved: () => void;
}): JSX.Element {
  const [first, setFirst] = useState(user.first_name ?? "");
  const [last, setLast] = useState(user.last_name ?? "");
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setFirst(user.first_name ?? "");
    setLast(user.last_name ?? "");
  }, [user.first_name, user.last_name]);

  const save = useMutation({
    mutationFn: async () =>
      api("/api/profile", {
        method: "PATCH",
        body: { user: { first_name: first.trim(), last_name: last.trim() } },
      }),
    onSuccess: () => {
      setSavedAt(Date.now());
      onSaved();
    },
  });

  const dirty =
    first.trim() !== (user.first_name ?? "") ||
    last.trim() !== (user.last_name ?? "");

  return (
    <Card>
      <CardHeader title="Identity" />
      <CardContent sx={{ display: "grid", gap: 2 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 2 }}>
          <Box component="label" sx={{ display: "block" }}>
            <Typography component="span" sx={{ fontSize: 12, fontWeight: 500, color: "text.secondary" }}>
              First name
            </Typography>
            <TextField
              value={first}
              onChange={(e) => setFirst(e.target.value)}
              fullWidth
              sx={{ mt: 0.5 }}
            />
          </Box>
          <Box component="label" sx={{ display: "block" }}>
            <Typography component="span" sx={{ fontSize: 12, fontWeight: 500, color: "text.secondary" }}>
              Last name
            </Typography>
            <TextField
              value={last}
              onChange={(e) => setLast(e.target.value)}
              fullWidth
              sx={{ mt: 0.5 }}
            />
          </Box>
        </Box>

        <Box>
          <Typography component="span" sx={{ fontSize: 12, fontWeight: 500, color: "text.secondary" }}>
            Email
          </Typography>
          <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              value={user.email}
              InputProps={{ readOnly: true }}
              disabled
              inputProps={{ "aria-label": "Email (read only)" }}
              fullWidth
            />
          </Box>
          <Typography sx={{ mt: 0.5, fontSize: "11px", color: "text.disabled" }}>
            Email is your sign-in identity and is managed centrally — contact a
            Super Admin to change it.
          </Typography>
        </Box>

        {user.role && (
          <Box>
            <Typography component="span" sx={{ fontSize: 12, fontWeight: 500, color: "text.secondary" }}>
              Role
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 14, color: "text.primary" }}>
              {user.role}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Button
            variant="contained"
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
          >
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
          {save.isError && (
            <Typography component="span" sx={{ fontSize: 12, color: "error.main" }}>
              {errMsg(save.error, "Could not save.")}
            </Typography>
          )}
          {savedAt !== null && !dirty && !save.isPending && !save.isError && (
            <Typography component="span" sx={{ fontSize: 12, color: "success.main" }}>
              Saved
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
